import React, { useCallback, useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import {
  fetchMyScratchPurchases,
  fetchMyScratchStats,
  isScratchPurchasePending,
  classifyScratchNet,
  type ScratchPurchase,
  type ScratchPlayerStats,
} from '../../services/scratchLotteryService'
import { HistoryCollapseCard, type HistoryDetailRow } from './HistoryCollapseCard'
import type { HistoryOutletContext } from './ModernHistoryLayout'

const PAGE_SIZE = 20

const formatNumber = (value: number | null | undefined, digits = 2) =>
  Number(value || 0).toLocaleString('th-TH', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('th-TH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const statusLabel = (item: ScratchPurchase) => {
  if (isScratchPurchasePending(item)) return 'ยังไม่เปิดผล'
  const status = String(item.status || '').toLowerCase()
  if (status === 'revealed') return 'เปิดผลแล้ว'
  if (status === 'cancelled') return 'ยกเลิก'
  if (status === 'expired') return 'หมดอายุ'
  if (status === 'failed') return 'ล้มเหลว'
  if (status.includes('debit')) return 'รอยืนยันการตัดเงิน'
  return item.status || '-'
}

const buildRows = (item: ScratchPurchase): HistoryDetailRow[] => {
  const pending = isScratchPurchasePending(item)
  const prize = Number(item.prizeAmount || 0)
  const price = Number(item.ticketPrice || 0)
  const outcome = classifyScratchNet(prize, price)
  const rows: HistoryDetailRow[] = [
    { label: 'สถานะ', value: statusLabel(item) },
    { label: 'ราคาการ์ด', value: `${formatNumber(price)} บาท` },
  ]

  if (pending) {
    rows.push({ label: 'รางวัล', value: 'ยังไม่เปิดผล' })
  } else {
    rows.push({
      label: 'รางวัล',
      value: `${formatNumber(prize)} บาท`,
      tone: prize > 0 ? 'positive' : 'default',
    })
    rows.push({
      label: outcome.label,
      value: `${formatNumber(outcome.net)} บาท`,
      tone: outcome.net >= 0 ? 'positive' : 'negative',
    })
  }

  if (item.theme) rows.push({ label: 'ธีม', value: String(item.theme) })
  rows.push({ label: 'วันที่ซื้อ', value: formatDateTime(item.createdAt || item.purchasedAt) })
  if (item.revealedAt) rows.push({ label: 'เปิดผลเมื่อ', value: formatDateTime(item.revealedAt) })
  return rows
}

/** ประวัติหวยขูดเมกา — อยู่กลุ่มประวัติเกมส์ที่ /history/games */
const HistoryScratchLotteryPage: React.FC = () => {
  useOutletContext<HistoryOutletContext>()
  const [items, setItems] = useState<ScratchPurchase[]>([])
  const [stats, setStats] = useState<ScratchPlayerStats | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadHistory = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true)
      setError(null)
      const [response, statsRes] = await Promise.all([
        fetchMyScratchPurchases({ page: pageNum, limit: PAGE_SIZE }),
        fetchMyScratchStats().catch(() => null),
      ])
      setItems(response.data || [])
      setPage(response.pagination?.page || pageNum)
      setTotalPages(Math.max(1, response.pagination?.totalPages || 1))
      if (statsRes?.success && statsRes.data) setStats(statsRes.data)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'โหลดประวัติไม่สำเร็จ')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHistory(1)
  }, [loadHistory])

  const handleCopyRef = async (refId: string) => {
    try {
      await navigator.clipboard.writeText(refId)
    } catch {
      // ignore
    }
  }

  return (
    <div className="htd-hist__stack">
      <h2 className="htd-hist__page-title">ประวัติหวยขูดเมกา</h2>

      <div className="htd-hist__panel">
        <div className="htd-hist__summary">
          <div>
            <strong>การ์ดหวยขูดของคุณ</strong>
            <span>ราคา · รางวัล · สุทธิ · สถานะ</span>
          </div>
          <div className="text-end">
            <Link to="/points/games/scratch-lottery" className="htd-hist__inline-link">
              ไปเล่นหวยขูด
            </Link>
          </div>
        </div>
        {stats && stats.ticketsBought > 0 && (
          <div className="mt-2" style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
            <div>
              เปิดผลแล้ว {stats.revealedCount} ใบ · ใช้ {formatNumber(stats.totalSpent)} ฿ · ได้{' '}
              {formatNumber(stats.totalPrizes)} ฿ · สุทธิ{' '}
              <strong style={{ color: stats.netProfit >= 0 ? '#16a34a' : '#dc2626' }}>
                {stats.netProfit >= 0 ? '+' : ''}
                {formatNumber(stats.netProfit)} ฿
              </strong>
            </div>
            <div style={{ opacity: 0.85 }}>
              กำไร {stats.profitCount} · คุ้มทุน {stats.breakEvenCount} · ขาดทุน {stats.lossCount}
            </div>
          </div>
        )}
      </div>

      <div className="htd-hist__list">
        {loading && (
          <div className="htd-hist__loading">
            <h4>
              <i className="fas fa-spinner fa-spin me-2" />
              กำลังโหลดประวัติหวยขูด...
            </h4>
          </div>
        )}

        {!loading && error && (
          <div className="htd-hist__shell">
            <h4>{error}</h4>
            <button type="button" className="htd-hist__retry-btn" onClick={() => void loadHistory(page)}>
              ลองโหลดอีกครั้ง
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="htd-hist__empty">
            <h4>ยังไม่มีประวัติหวยขูด</h4>
            <p>เมื่อซื้อการ์ดหวยขูด รายการจะแสดงที่นี่</p>
            <Link to="/points/games/scratch-lottery" className="htd-hist__inline-link">
              ไปเล่นหวยขูด
            </Link>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="htd-hist__panel mb-2">
              <div className="htd-hist__summary">
                <div>
                  <strong>
                    พบ {items.length} รายการ
                    {totalPages > 1 ? ` (หน้า ${page}/${totalPages})` : ''}
                  </strong>
                  <span>แตะการ์ดเพื่อดูรายละเอียด</span>
                </div>
              </div>
            </div>

            {items.map((item) => {
              const pending = isScratchPurchasePending(item)
              const prize = Number(item.prizeAmount || 0)
              const price = Number(item.ticketPrice || 0)
              const leftIcon = pending ? 'pending' : prize > 0 ? 'success' : 'reject'
              return (
                <HistoryCollapseCard
                  key={item.purchaseId}
                  id={item.purchaseId}
                  leftIcon={leftIcon}
                  refId={item.purchaseId}
                  onCopyRef={() => void handleCopyRef(item.purchaseId)}
                  rows={buildRows(item)}
                  header={(
                    <div className="w-100 pe-md-4">
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
                        หวยขูดเมกา ·{' '}
                        {pending ? 'ยังไม่เปิดผล' : `รางวัล ${formatNumber(prize)} บาท`}
                      </div>
                      <small style={{ color: 'rgba(186,230,253,0.75)', display: 'block' }}>
                        ราคา {formatNumber(price)} บาท · {statusLabel(item)} ·{' '}
                        {formatDateTime(item.createdAt)}
                      </small>
                      {pending && (
                        <Link
                          to="/points/games/scratch-lottery"
                          className="htd-hist__inline-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          ขูดต่อให้จบ
                        </Link>
                      )}
                    </div>
                  )}
                />
              )
            })}

            {totalPages > 1 && (
              <div className="d-flex justify-content-center gap-2 mt-2">
                <button
                  type="button"
                  className="htd-hist__retry-btn"
                  disabled={page <= 1}
                  onClick={() => void loadHistory(page - 1)}
                >
                  ก่อนหน้า
                </button>
                <button
                  type="button"
                  className="htd-hist__retry-btn"
                  disabled={page >= totalPages}
                  onClick={() => void loadHistory(page + 1)}
                >
                  ถัดไป
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default HistoryScratchLotteryPage
